import { useTranslation } from 'react-i18next';

const complianceImg = 'http://geel.fp.08wx.cn/images/80c4a6f4b3b456cc6345d9d008a95aed.jpg';

const COPY = {
  'zh-CN': {
    title: '多辖区合规框架', highlight: '全球发行，本地合规',
    desc: '深度研究 15+ 司法辖区的数字资产监管法规，为每个项目量身定制合规路径，从牌照申请、法律文件、KYC/AML 到持续合规报告，提供全生命周期合规保障。',
    badgeTitle: '合规保障体系', badgeSub: '15+ 司法辖区 · 全程陪跑',
    jTags: ['MAS 牌照', 'SFC 注册', 'MiCA 合规', 'FATF 规则', '投资者认证', '自动报告'],
    frameworks: [
      ['新加坡', 'MAS', 'CMS 牌照 · Digital Token'], ['香港', 'SFC', 'VASP 牌照 · STO 框架'], ['欧盟', 'MiCA', 'DASP 注册 · MiCA 合规'],
      ['开曼群岛', 'CIMA', 'Virtual Asset Law'], ['英属维京群岛', 'FSC', 'VAS 框架 · 基金牌照'], ['美国', 'SEC/FinCEN', 'Reg D / Reg S 豁免'],
    ],
    features: [
      ['法律文件体系', '募集说明书、投资协议、代币认购合同等全套法律文件模板，覆盖主流辖区'],
      ['KYC/AML 合规流程', '身份核验、反洗钱筛查、制裁名单过滤，满足 FATF 旅行规则要求'],
      ['监管报告自动化', '自动生成监管报告、持有人名册、资产净值报告，定期提交无忧'],
      ['投资者资质认证', '专业投资者/合格投资者在线认证，白名单机制，精准限定投资范围'],
    ],
  },
  en: {
    title: 'Multi-Jurisdiction Compliance', highlight: 'Global Issuance, Local Compliance',
    desc: 'We design compliance paths per project across 15+ jurisdictions, covering licensing, legal documentation, KYC/AML, and ongoing reporting for end-to-end compliant operations.',
    badgeTitle: 'Compliance Assurance', badgeSub: '15+ Jurisdictions · End-to-End Support',
    jTags: ['MAS License', 'SFC Registration', 'MiCA Compliance', 'FATF Rules', 'Investor Verification', 'Auto Reporting'],
    frameworks: [
      ['Singapore', 'MAS', 'CMS License · Digital Token'], ['Hong Kong', 'SFC', 'VASP License · STO Framework'], ['EU', 'MiCA', 'DASP Registration · MiCA'],
      ['Cayman Islands', 'CIMA', 'Virtual Asset Law'], ['BVI', 'FSC', 'VAS Framework · Fund License'], ['United States', 'SEC/FinCEN', 'Reg D / Reg S Exemptions'],
    ],
    features: [
      ['Legal Documentation Suite', 'Prospectus, investor agreements, and token subscription contracts tailored by jurisdiction.'],
      ['KYC/AML Workflow', 'Identity verification, AML screening, and sanctions filtering aligned with FATF requirements.'],
      ['Regulatory Reporting Automation', 'Automated holder registry, NAV reports, and periodic submission packages.'],
      ['Investor Qualification', 'Online accredited/professional investor verification with whitelist controls.'],
    ],
  },
  'zh-TW': {
    title: '多轄區合規框架', highlight: '全球發行，本地合規',
    desc: '深度研究 15+ 司法轄區的數字資產監管法規，為每個項目量身定制合規路徑，從牌照申請、法律文件、KYC/AML 到持續合規報告，提供全生命週期合規保障。',
    badgeTitle: '合規保障體系', badgeSub: '15+ 司法轄區 · 全程陪跑',
    jTags: ['MAS 牌照', 'SFC 註冊', 'MiCA 合規', 'FATF 規則', '投資者認證', '自動報告'],
    frameworks: [
      ['新加坡', 'MAS', 'CMS 牌照 · Digital Token'], ['香港', 'SFC', 'VASP 牌照 · STO 框架'], ['歐盟', 'MiCA', 'DASP 註冊 · MiCA 合規'],
      ['開曼群島', 'CIMA', 'Virtual Asset Law'], ['英屬維京群島', 'FSC', 'VAS 框架 · 基金牌照'], ['美國', 'SEC/FinCEN', 'Reg D / Reg S 豁免'],
    ],
    features: [
      ['法律文件體系', '募集說明書、投資協議、代幣認購合同等全套法律文件模板，覆蓋主流轄區'],
      ['KYC/AML 合規流程', '身份核驗、反洗錢篩查、制裁名單過濾，滿足 FATF 旅行規則要求'],
      ['監管報告自動化', '自動生成監管報告、持有人名冊、資產淨值報告，定期提交無憂'],
      ['投資者資質認證', '專業投資者/合格投資者線上認證，白名單機制，精準限定投資範圍'],
    ],
  },
} as const;

const ICONS = ['ri-file-list-3-line', 'ri-user-follow-line', 'ri-government-line', 'ri-shield-keyhole-line'];
const COLORS = ['#FF2D78', '#FF8C42', '#FF2D78', '#FF8C42', '#FF2D78', '#FF8C42'];
function getLocale(lng: string) { if (lng.startsWith('en')) return 'en'; if (lng.startsWith('zh-TW')) return 'zh-TW'; return 'zh-CN'; }

export default function RwaComplianceSection() {
  const { i18n } = useTranslation();
  const c = COPY[getLocale(i18n.resolvedLanguage || i18n.language || 'zh-CN')];
  return (
    <section id="compliance" className="bg-white py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-14 xl:gap-20">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-full">
              <i className="ri-government-line text-accent text-xs" />
              <span className="text-accent text-xs font-medium uppercase tracking-widest">Compliance Framework</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 leading-tight">{c.title}<br /><span className="text-accent">{c.highlight}</span></h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">{c.desc}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {c.frameworks.map((f, i) => (
                <div key={i} className="bg-light-alt border border-primary/10 rounded-xl p-4 flex items-center gap-4 hover:border-accent/25 transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${COLORS[i]}12` }}><i className="ri-bank-line text-base" style={{ color: COLORS[i] }} /></div>
                  <div><div className="flex items-center gap-2 mb-0.5"><span className="text-gray-800 text-sm font-medium">{f[0]}</span><span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: `${COLORS[i]}12`, color: COLORS[i] }}>{f[1]}</span></div><div className="text-gray-400 text-xs">{f[2]}</div></div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.features.map((f, i) => (
                <div key={i} className="bg-light-alt border border-primary/10 rounded-xl p-4 hover:border-accent/25 transition-all duration-200">
                  <div className="flex items-start gap-3"><div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent/10 flex-shrink-0 mt-0.5"><i className={`${ICONS[i]} text-sm text-accent`} /></div><div><h4 className="text-gray-800 text-sm font-medium mb-1">{f[0]}</h4><p className="text-gray-500 text-xs leading-relaxed">{f[1]}</p></div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:w-[420px] xl:w-[500px] flex-shrink-0">
            <div className="relative rounded-2xl overflow-hidden border border-accent/15 sticky top-24">
              <div className="w-full h-[340px]"><img src={complianceImg} alt="Compliance Framework" className="w-full h-full object-cover object-top" /><div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" /></div>
              <div className="absolute top-4 left-4 bg-white/95 border border-accent/25 rounded-xl px-4 py-3 backdrop-blur-sm"><div className="flex items-center gap-2 mb-1"><i className="ri-government-line text-accent text-sm" /><span className="text-gray-800 text-xs font-medium">{c.badgeTitle}</span></div><div className="text-gray-400 text-xs">{c.badgeSub}</div></div>
              <div className="absolute bottom-6 left-4 right-4 flex flex-wrap gap-2">{c.jTags.map((tag, i) => (<span key={i} className="px-2.5 py-1 bg-white/95 border border-accent/20 text-accent text-xs rounded-lg backdrop-blur-sm">{tag}</span>))}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
